/**
 * Per-user reading & quiz completion. localStorage stays the instant-read cache (progress rings,
 * "続きから", 必読 gate), and every change is MIRRORED to the server (PUT /api/progress/:manualId,
 * fire-and-forget) so 学習管理 has real org-wide evidence. Reads are always served locally.
 *
 * Keyed by the caller's JWT sub so two accounts on a shared iPad don't collide.
 */
import { getClaims, progress as progressApi } from "./api.js";

function sub(): string {
  return getClaims()?.sub ?? "anon";
}
const readKey = (id: string) => `tebiki_read_${sub()}_${id}`;
const quizKey = (id: string) => `tebiki_quiz_${sub()}_${id}`;
const viewModeKey = () => `tebiki_readmode_${sub()}`;

/** Reader layout preference (personal, per-user). 'doc' = continuous manual format (default). */
export type ReaderViewMode = "doc" | "paged";

export interface ReadState {
  /** last step index viewed (0-based) — for "続きから" */
  lastStep: number;
  /** count of distinct steps viewed */
  completed: number;
  total: number;
  /** epoch ms when the reader reached the last step */
  finishedAt?: number;
}

export interface QuizState {
  bestScore: number;
  passed: boolean;
  attempts: number;
  lastAt: number;
  /** manual version the best attempt was taken against (re-quiz on version bump) */
  version?: number;
}

function readJson<T>(key: string): T | null {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : null;
  } catch {
    return null;
  }
}
function writeJson(key: string, value: unknown): void {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* private mode / quota — progress is best-effort */
  }
}

export function getRead(manualId: string): ReadState | null {
  return readJson<ReadState>(readKey(manualId));
}

// ---- server mirror (fire-and-forget; the local cache stays the read path) ----

const syncTimers = new Map<string, number>();

async function pushProgress(manualId: string, keepalive = false): Promise<void> {
  if (!getClaims()) return; // no session (e.g. expired) — keep it local
  const r = getRead(manualId);
  const q = getQuiz(manualId);
  if (!r && !q) return;
  try {
    await progressApi.put(
      manualId,
      {
        readPct: readPct(manualId),
        lastStep: r?.lastStep ?? 0,
        quizScore: q ? q.bestScore : null,
        quizPassed: q?.passed ?? false,
      },
      { keepalive },
    );
  } catch {
    /* offline / API down — the next change retries; localStorage stays authoritative locally */
  }
}

/** Debounced mirror: scroll-driven markRead fires often; one PUT ~2s after the last change. */
function scheduleSync(manualId: string): void {
  if (typeof window === "undefined") return;
  const prev = syncTimers.get(manualId);
  if (prev) window.clearTimeout(prev);
  syncTimers.set(
    manualId,
    window.setTimeout(() => {
      syncTimers.delete(manualId);
      void pushProgress(manualId);
    }, 2000),
  );
}

// Flush pending debounces when the page is going away — otherwise the reader who just FINISHED
// (the moment that matters most for 学習証跡) loses the final PUT by closing the tab within 2s.
if (typeof window !== "undefined") {
  window.addEventListener("pagehide", () => {
    for (const [manualId, timer] of syncTimers) {
      window.clearTimeout(timer);
      void pushProgress(manualId, true); // keepalive request survives the unload
    }
    syncTimers.clear();
  });
}

// ---- server hydration (new device / fresh browser): serve rings & 修了 from the mirror ----

const serverKey = () => `tebiki_progress_server_${sub()}`;

interface ServerEntryLite {
  readPct: number;
  lastStep: number;
  quizScore: number | null;
  quizPassed: boolean;
  completedAt: number | null;
}

function serverMap(): Record<string, ServerEntryLite> {
  return readJson<Record<string, ServerEntryLite>>(serverKey()) ?? {};
}
function serverEntry(manualId: string): ServerEntryLite | null {
  return serverMap()[manualId] ?? null;
}

let hydratedThisSession = false;

/**
 * Pull the caller's server-side progress once per session and cache it locally, so a new device
 * shows the same rings / 続きから / 修了 the person earned elsewhere. Local records stay
 * authoritative; the server map is a max-merge fallback consulted by readPct/isComplete.
 */
export async function hydrateProgress(): Promise<void> {
  if (hydratedThisSession || typeof window === "undefined" || !getClaims()) return;
  hydratedThisSession = true;
  try {
    const entries = await progressApi.me();
    const map: Record<string, ServerEntryLite> = {};
    for (const e of entries) {
      map[e.manualId] = {
        readPct: e.readPct,
        lastStep: e.lastStep,
        quizScore: e.quizScore,
        quizPassed: e.quizPassed,
        completedAt: e.completedAt,
      };
    }
    writeJson(serverKey(), map);
  } catch {
    hydratedThisSession = false; // retry on the next screen that calls hydrateProgress
  }
}

/** Merge a reading position; tracks the max distinct steps seen + last index. */
export function markRead(manualId: string, stepIdx: number, total: number): ReadState {
  const prev = getRead(manualId);
  const completed = Math.max(prev?.completed ?? 0, Math.min(stepIdx + 1, total));
  const state: ReadState = {
    lastStep: stepIdx,
    completed,
    total,
    finishedAt: completed >= total ? (prev?.finishedAt ?? Date.now()) : prev?.finishedAt,
  };
  writeJson(readKey(manualId), state);
  scheduleSync(manualId);
  return state;
}

export function getQuiz(manualId: string): QuizState | null {
  return readJson<QuizState>(quizKey(manualId));
}

/** Record a quiz attempt, keeping the BEST score; passing once stays passed. */
export function recordQuiz(manualId: string, score: number, passed: boolean, version?: number): QuizState {
  const prev = getQuiz(manualId);
  const state: QuizState = {
    bestScore: Math.max(prev?.bestScore ?? 0, score),
    passed: (prev?.passed ?? false) || passed,
    attempts: (prev?.attempts ?? 0) + 1,
    lastAt: Date.now(),
    version,
  };
  writeJson(quizKey(manualId), state);
  void pushProgress(manualId); // quiz results matter for 修了証跡 — mirror immediately
  return state;
}

/** 0..100 reading completion for one manual (local record, else the server mirror). */
export function readPct(manualId: string): number {
  const r = getRead(manualId);
  const local = r && r.total > 0 ? Math.round((r.completed / r.total) * 100) : 0;
  const server = serverEntry(manualId)?.readPct ?? 0;
  return Math.max(local, server);
}

/** Last step index for 続きから — local record, else the server mirror from another device. */
export function resumeStep(manualId: string): number {
  return getRead(manualId)?.lastStep ?? serverEntry(manualId)?.lastStep ?? 0;
}

/** Personal reader layout preference (defaults to 'doc' = manual format). */
export function getViewMode(): ReaderViewMode {
  return readJson<ReaderViewMode>(viewModeKey()) === "paged" ? "paged" : "doc";
}
export function setViewMode(mode: ReaderViewMode): void {
  writeJson(viewModeKey(), mode);
}

/** 修了 gate: finished reading AND quiz passed (local record, else the server mirror). */
export function isComplete(manualId: string): boolean {
  const r = getRead(manualId);
  const q = getQuiz(manualId);
  const readDone = !!r && r.total > 0 && r.completed >= r.total;
  if (readDone && !!q?.passed) return true;
  return !!serverEntry(manualId)?.completedAt;
}
